'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, Database, TrendingUp, Zap, CheckCircle, Code, BarChart3, Cpu, Cloud, Server, HardDrive, Activity } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const bigDataData = {
  title: "Big Data",
  subtitle: "Scalable data processing and analytics solutions that transform massive datasets into actionable business insights",
  headerGradient: "bg-gradient-to-r from-amber-500 via-orange-500 to-red-500",
  accentColor: "text-orange-600",
  buttonColor: "bg-orange-600 hover:bg-orange-700",
  
  overview: "Big Data technologies enable organizations to process, analyze, and extract valuable insights from massive volumes of structured and unstructured data. Our Big Data solutions help businesses harness the power of their data assets to drive informed decision-making, optimize operations, and unlock new revenue opportunities at scale.",
  
  capabilities: [
    {
      name: "Hadoop Ecosystem",
      description: "Distributed storage and processing framework for handling large datasets across clusters",
      icon: Database,
      applications: ["HDFS", "MapReduce", "YARN", "Hive", "HBase"],
      useCases: ["Data warehousing", "ETL processing", "Log analysis", "Batch processing"]
    },
    {
      name: "Apache Spark",
      description: "Unified analytics engine for large-scale data processing with in-memory computing",
      icon: Zap,
      applications: ["Spark SQL", "Spark Streaming", "MLlib", "GraphX"],
      useCases: ["Real-time analytics", "Machine learning", "Stream processing", "Interactive queries"]
    },
    {
      name: "Real-time Processing",
      description: "Stream processing systems for analyzing data as it arrives in real-time",
      icon: Activity,
      applications: ["Apache Kafka", "Apache Storm", "Apache Flink", "Kinesis"],
      useCases: ["Live dashboards", "Fraud detection", "IoT monitoring", "Event-driven architecture"]
    }
  ],
  
  technologies: [
    {
      category: "Storage Systems",
      tools: ["HDFS", "Amazon S3", "Azure Data Lake", "Google Cloud Storage", "Cassandra", "MongoDB"],
      description: "Distributed storage solutions for massive data volumes"
    },
    {
      category: "Processing Frameworks",
      tools: ["Apache Spark", "Apache Hadoop", "Apache Flink", "Apache Storm", "Databricks"],
      description: "Scalable data processing engines for batch and stream processing"
    },
    {
      category: "Data Integration",
      tools: ["Apache Kafka", "Apache NiFi", "Talend", "Informatica", "AWS Glue", "Azure Data Factory"],
      description: "Tools for data ingestion, transformation, and pipeline orchestration"
    },
    {
      category: "Analytics & Querying",
      tools: ["Apache Hive", "Presto", "Apache Drill", "Elasticsearch", "ClickHouse", "BigQuery"],
      description: "SQL engines and analytics platforms for big data querying"
    },
    {
      category: "Workflow Management",
      tools: ["Apache Airflow", "Luigi", "Oozie", "Prefect", "Dagster", "AWS Step Functions"],
      description: "Orchestration tools for complex data processing workflows"
    },
    {
      category: "Visualization & BI",
      tools: ["Tableau", "Power BI", "Apache Superset", "Grafana", "Looker", "Qlik"],
      description: "Business intelligence and data visualization platforms"
    }
  ],
  
  industries: [
    {
      name: "Financial Services",
      applications: ["Risk analytics", "Fraud detection", "Algorithmic trading", "Regulatory reporting"],
      impact: "70% faster risk calculations, 85% improvement in fraud detection accuracy"
    },
    {
      name: "Retail & E-commerce",
      applications: ["Customer analytics", "Inventory optimization", "Price optimization", "Recommendation engines"],
      impact: "40% increase in customer lifetime value, 25% reduction in inventory costs"
    },
    {
      name: "Healthcare",
      applications: ["Clinical analytics", "Population health", "Drug discovery", "Medical imaging analysis"],
      impact: "60% faster clinical trials, 30% improvement in patient outcomes"
    },
    {
      name: "Telecommunications",
      applications: ["Network optimization", "Customer churn analysis", "Usage analytics", "Quality monitoring"],
      impact: "35% reduction in network downtime, 50% improvement in customer retention"
    },
    {
      name: "Manufacturing",
      applications: ["Predictive maintenance", "Quality control", "Supply chain optimization", "IoT analytics"],
      impact: "45% reduction in equipment downtime, 30% improvement in production efficiency"
    },
    {
      name: "Media & Entertainment",
      applications: ["Content analytics", "Audience segmentation", "Recommendation systems", "Ad optimization"],
      impact: "55% increase in content engagement, 40% improvement in ad targeting"
    }
  ],
  
  dataTypes: [
    {
      type: "Structured Data",
      description: "Organized data in tables, databases, and spreadsheets",
      examples: ["Transactional records", "Customer databases", "Financial data", "Inventory systems"],
      volume: "Terabytes to Petabytes"
    },
    {
      type: "Semi-Structured Data",
      description: "Data with some organizational properties but not fully structured",
      examples: ["JSON files", "XML documents", "Log files", "Email metadata"],
      volume: "Gigabytes to Exabytes"
    },
    {
      type: "Unstructured Data",
      description: "Data without predefined structure or organization",
      examples: ["Text documents", "Images", "Videos", "Social media posts"],
      volume: "Petabytes to Zettabytes"
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Data Ingestion",
      description: "Collect and import data from various sources into the big data platform",
      details: ["Batch data loading", "Real-time streaming", "API integrations", "File transfers and ETL processes"]
    },
    {
      step: "2", 
      title: "Data Storage & Management",
      description: "Store and organize data in distributed storage systems with proper governance",
      details: ["Distributed file systems", "Data lakes and warehouses", "Metadata management", "Data quality and lineage"]
    },
    {
      step: "3",
      title: "Data Processing & Analytics",
      description: "Process and analyze data using distributed computing frameworks",
      details: ["Batch processing workflows", "Real-time stream processing", "Machine learning pipelines", "Statistical analysis"]
    },
    {
      step: "4",
      title: "Insights & Visualization",
      description: "Transform processed data into actionable insights and visual dashboards",
      details: ["Interactive dashboards", "Automated reporting", "Business intelligence", "Data-driven recommendations"]
    }
  ],
  
  benefits: [
    "Process massive datasets efficiently",
    "Scale horizontally across clusters", 
    "Reduce data processing costs",
    "Enable real-time decision making",
    "Improve data quality and governance",
    "Accelerate time-to-insight",
    "Support diverse data formats",
    "Enhance business intelligence capabilities"
  ],
  
  challenges: [
    {
      challenge: "Data Volume",
      solution: "Distributed storage and parallel processing",
      description: "Handle petabytes of data across multiple nodes"
    },
    {
      challenge: "Data Velocity",
      solution: "Stream processing and real-time analytics",
      description: "Process high-speed data streams in real-time"
    },
    {
      challenge: "Data Variety",
      solution: "Schema-on-read and flexible data models",
      description: "Support structured, semi-structured, and unstructured data"
    },
    {
      challenge: "Data Veracity",
      solution: "Data quality frameworks and validation",
      description: "Ensure data accuracy and reliability"
    }
  ]
};

export default function BigDataPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${bigDataData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, white 2px, transparent 2px),
                             radial-gradient(circle at 80% 70%, white 2px, transparent 2px),
                             radial-gradient(circle at 40% 80%, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px, 40px 40px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(bigDataData.title)}
              </h1>
              <p className="text-xl text-orange-100 mb-8 leading-relaxed">
                {localize(bigDataData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["Hadoop", "Spark", "Analytics", "Data Processing"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Database className="w-12 h-12 mx-auto mb-3 text-yellow-200" />
                    <div className="text-2xl font-bold">100TB+</div>
                    <div className="text-sm text-orange-100">{localize("Data Processed")}</div>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-yellow-200" />
                    <div className="text-2xl font-bold">50x</div>
                    <div className="text-sm text-orange-100">{localize("Faster Analytics")}</div>
                  </div>
                  <div className="text-center">
                    <Server className="w-12 h-12 mx-auto mb-3 text-yellow-200" />
                    <div className="text-2xl font-bold">1000+</div>
                    <div className="text-sm text-orange-100">{localize("Nodes")}</div>
                  </div>
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-yellow-200" />
                    <div className="text-2xl font-bold">99.9%</div>
                    <div className="text-sm text-orange-100">{localize("Uptime")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${bigDataData.accentColor}`}>
              {localize("What is Big Data?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(bigDataData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core Big Data Technologies")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {bigDataData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-orange-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-orange-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Technologies")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Data Types Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Types of Big Data")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {bigDataData.dataTypes.map((dataType, index) => (
              <motion.div key={dataType.type} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <HardDrive className="w-5 h-5 text-orange-600" />
                      {localize(dataType.type)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(dataType.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Examples")}
                        </h4>
                        <ul className="space-y-1">
                          {dataType.examples.map((example) => (
                            <li key={example} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(example)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Typical Volume")}
                        </h4>
                        <p className="text-sm text-orange-700 font-medium">{localize(dataType.volume)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Big Data Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bigDataData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-orange-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Big Data Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Big Data Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {bigDataData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-green-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bigDataData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-green-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our Big Data Implementation Process")}
          </h2>
          
          <div className="space-y-8">
            {bigDataData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 md:p-12 border border-orange-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose Big Data Solutions?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {bigDataData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Unlock the Power of Your Big Data?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our Big Data experts help you build scalable data processing solutions that transform massive datasets into valuable business insights.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${bigDataData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Your Big Data Project")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 